import React from 'react';
import PropTypes from 'prop-types';
import useFlipper from '../../../../../../modules/react/hooks/useFlipper';
import useIntegrations from '../../../../../../modules/react/hooks/useIntegrations';
import { FLIPPER_VOYAGER_RENTAL_SHORT_FORM } from '../../../../../../constants/flippers';
import Call from './actions/Call';
import MoveIn from './actions/MoveIn';
import Reservation from './actions/Reservation';
import RentalShortForm from '../../../../../experiments/RentalShortForm';
import styles from './ActionButtons.scss';

const propTypes = {
  actionButtons: PropTypes.shape({
    call: PropTypes.string.isRequired,
    moveIn: PropTypes.string.isRequired,
    reserve: PropTypes.string.isRequired,
  }).isRequired,
  availableUnitsCount: PropTypes.number.isRequired,
  closeSelectedUnitModal: PropTypes.func,
  showRentalShortFormModal: PropTypes.bool,
  toggleRentalShortFormModal: PropTypes.func,
  integrations: PropTypes.arrayOf(PropTypes.shape({
    alert: PropTypes.shape({
      active: PropTypes.bool.isRequired,
    }),
    name: PropTypes.string.isRequired,
  })).isRequired,
  facility: PropTypes.shape({
    discountPlans: PropTypes.arrayOf(PropTypes.shape({
      autoApply: PropTypes.bool,
      moveInOnly: PropTypes.bool,
    })),
    phone: PropTypes.string,
    settings: PropTypes.shape({
      softwareProvider: PropTypes.string.isRequired,
      voyagerOnly: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
};

const defaultProps = {
  closeSelectedUnitModal: undefined,
  showRentalShortFormModal: false,
  toggleRentalShortFormModal: undefined,
};

const ActionButtons = ({
  actionButtons,
  availableUnitsCount,
  closeSelectedUnitModal,
  facility,
  inModalContext,
  showRentalShortFormModal,
  toggleRentalShortFormModal,
  ...rest
}) => {
  const { integrations } = useIntegrations();

  const { settings: { softwareProvider, voyagerOnly } } = facility;

  const facilityIntegration = integrations.find(({ name }) => name === softwareProvider);
  const integrationAlerting = facilityIntegration && facilityIntegration.alert.active;

  // We show a modal for mobile users. In this context, we only want to give them a call
  // button if the integration is down.
  if (integrationAlerting && inModalContext) {
    return (
      <Call
        {...rest}
        facility={facility}
        text={actionButtons.call}
        useTel
      />
    );
  }

  // For desktop users, we want to show a message inviting them to call the facility if the
  // integration is down.
  const { phone } = facility;

  if (integrationAlerting && !inModalContext) {
    const message = phone
      ? (<a href={`tel:${phone}`}>{phone}</a>)
      : 'the facility ';

    return (
      <div className={`d-none d-md-block ${styles.downtimeMsg}`}>
        Please call {message} to reserve a unit
      </div>
    );
  }

  const shortFormEnabled = useFlipper(FLIPPER_VOYAGER_RENTAL_SHORT_FORM);
  const { id: unitGroupId, reservationUrl } = rest;

  const { settings: { callActionThreshold, disableReservations, enableMoveIns } } = facility;
  const canInteract = availableUnitsCount >= callActionThreshold;
  const showMoveInButton = canInteract && enableMoveIns;
  const showReserveButton = canInteract && !disableReservations;
  const showCallButton = !showMoveInButton && !showReserveButton;

  // The short form requires actual (aka "hard") reservations in storEDGE, so we only want
  // to enter that flow if the flipper is on and the facility settings allow it.
  if (shortFormEnabled && !voyagerOnly) {
    return (
      <RentalShortForm
        showCallButton={showCallButton}
        closeSelectedUnitModal={closeSelectedUnitModal}
        facility={facility}
        inModalContext={inModalContext}
        moveInText={actionButtons.moveIn}
        reserveText={actionButtons.reserve}
        reserveUrl={reservationUrl}
        unitGroupId={unitGroupId}
        callText={actionButtons.call}
        showRentalShortFormModal={showRentalShortFormModal}
        toggleRentalShortFormModal={toggleRentalShortFormModal}
        {...rest}
      />
    );
  }

  return (
    <>
      {showMoveInButton && (
        <MoveIn
          {...rest}
          facility={facility}
          text={actionButtons.moveIn}
        />
      )}
      {showReserveButton && (
        <Reservation
          {...rest}
          closeSelectedUnitModal={closeSelectedUnitModal}
          facility={facility}
          inModalContext={inModalContext}
          text={actionButtons.reserve}
          moveInText={actionButtons.moveIn}
        />
      )}
      {showCallButton && (
        <Call
          {...rest}
          facility={facility}
          text={actionButtons.call}
          useTel={inModalContext}
        />
      )}
    </>
  );
};

ActionButtons.propTypes = propTypes;
ActionButtons.defaultProps = defaultProps;

export default ActionButtons;
